import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { useTheme } from "../app/context/ThemeContext";
import { useChat } from "../app/context/ChatContext";
import { useContacts } from "../app/context/ContactsContext";
import { usePins } from "../app/context/PinsContext";
import type { ChatReplyDraft, MessageReactionBadge } from "../app/state/domainTypes";
import type { ChatAttachmentDraft } from "../app/types";
import {
  apiDeleteWorkspaceTopic,
  apiFetchPinnedMessages,
  apiFetchWorkspaceMembers,
  apiPinMessage,
  apiRenameWorkspaceTopic,
  apiRemoveContact,
  apiUnpinMessage,
  buildPinnedMessageContextId,
  type WorkspaceMemberItem,
} from "../lib/authApi";
import { removeContact as removeContactLocal } from "../lib/db/contacts";
import type { ContactRow, MessageRow } from "../lib/db";
import { envelopeReactionMeta, envelopeReplyToId, isTombstonePlaintext, previewTextForReply } from "../lib/crypto";
import {
  avatarUrlsPrimaryFallback,
  peerAvatarImageSources,
  type AvatarUrls,
} from "../lib/avatarDisplay";
import { type PresenceStatus } from "../styles/theme";
import { useVault } from "../app/context/VaultContext";
import type { ConversationCapabilities } from "../app/state/conversationCapabilities";
import { AppIcon } from "./AppIcon";
import { MobileEntityHeader, type MobileEntityHeaderMenuItem } from "./MobileEntityHeader";
import { MessageBubble } from "./MessageBubble";
import { VoiceRecorderButton } from "./VoiceRecorderButton";
import { WorkspaceMembersPanel } from "./WorkspaceMembersPanel";
import {
  WorkspaceMessageSearchPanel,
  type WorkspaceSearchSelectArgs,
} from "./WorkspaceMessageSearchPanel";
import { PinnedMessagesPanel } from "./PinnedMessagesPanel";

export type { ChatAttachmentDraft, ChatFileDraft, ChatImageDraft } from "../app/types";

type ChatViewProps = {
  conversationCapabilities: ConversationCapabilities;
  selectedContact: ContactRow | undefined;
  /** Открытый workspace topic (без DM-контакта): отдельный header и пустое состояние. */
  workspaceTopicView?: {
    workspaceId: string;
    topicId: string;
    workspaceName: string;
    topicName: string;
    /** Role in workspace (from shell sidebar) for gating menu actions. */
    workspaceRole?: string;
    canCreateTopics?: boolean;
    selectedTopicKey?: { workspaceId: string; topicId: string } | null;
    /** Refetch topic list metadata when workspace messages arrive over WS. */
    topicsRefreshEpoch?: number;
  };
  /** Личное хранилище (vault): чат с собой, без контакта в списке. */
  vaultTopicView?: { vaultId: string; vaultName: string };
  /** Скачивание ciphertext для E2E-вложений в vault. */
  fetchE2eEncryptedBlob?: (blobId: string) => Promise<ArrayBuffer>;
  presenceStatus: PresenceStatus;
  messages: MessageRow[];
  chatHasMoreOlder: boolean;
  conversationLoading: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  currentUserId: string;
  currentUsername: string;
  currentUserAvatarUrl?: string | null;
  /** When set, used with `currentUserAvatarUrl` fallback for correct primary/terminal URLs. */
  currentUserAvatarUrls?: AvatarUrls | null;
  chatInput: string;
  chatAttachmentDraft: ChatAttachmentDraft | null;
  chatReplyTo: ChatReplyDraft | null;
  onReplyToMessage: (message: MessageRow) => void;
  onForwardMessage?: (message: MessageRow) => void;
  onToggleReaction: (message: MessageRow, emoji: string, remove: boolean) => void;
  onCancelReply: () => void;
  isMobile: boolean;
  voiceCallEnabled?: boolean;
  onVoiceCall?: () => void;
  onBack: () => void;
  onChatInput: (value: string) => void;
  onPickImage: (file: File | null) => void;
  onPickFile: (file: File | null) => void;
  onRemoveImage: () => void;
  onSend: () => void;
  onSendVoice?: (plainBytes: Uint8Array, mime: string, durationMs: number, waveform: number[]) => void;
  onSelectWorkspaceSearchResult?: (args: WorkspaceSearchSelectArgs) => void;
  requestedJumpMessageId?: string | null;
  onJumpHandled?: (messageId: string) => void;
  /** After topic deleted (mobile): parent may open topics list. */
  onWorkspaceTopicDeleted?: () => void;
  /** After topic renamed: parent updates header labels / topic list epoch. */
  onWorkspaceTopicRenamed?: (topicName: string) => void;
  /** Workspace topic: серверные реакции (пусто в DM). */
  workspaceReactionsByMessageId?: Record<string, MessageReactionBadge[]>;
  /** Shared topic pin (owner/admin); server-side. */
  sharedTopicPinned?: boolean;
  onToggleSharedTopicPin?: () => void;
};

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function formatUploadBytes(bytes: number | null): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

function renderConversationLoadingState() {
  return (
    <div className="chat-empty chat-empty--inline chat-empty--loading" aria-live="polite" aria-busy="true">
      <div className="chat-empty__skeleton" aria-hidden="true">
        <span className="chat-empty__skeleton-line chat-empty__skeleton-line--lg" />
        <span className="chat-empty__skeleton-line chat-empty__skeleton-line--sm" />
        <span className="chat-empty__skeleton-line chat-empty__skeleton-line--md" />
      </div>
      <span className="chat-empty__hint">загружаем переписку…</span>
    </div>
  );
}

const NEAR_BOTTOM_PX = 72;
/** Fallback по scrollTop; основной триггер — IntersectionObserver на `.chat-shell__oldest-sentinel`. */
const LOAD_OLDER_TOP_PX = 320;
const MENU_SCROLL_CLOSE_PX = 36;

/** Последнее сообщение в ленте, не являющееся служебной «реакцией» (реакции дописываются в конец массива). */
function lastNonReactionTailId(messages: MessageRow[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (envelopeReactionMeta(messages[i].plaintext) === null) {
      return messages[i].id;
    }
  }
  return undefined;
}

/** Подгрузка старых: близко к maxScroll (в column-reverse это визуально «верх» с более старыми). */
function isNearOldestEdge(el: HTMLElement): boolean {
  const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
  if (maxScroll <= 0) return true;
  const dist = maxScroll - el.scrollTop;
  return dist <= LOAD_OLDER_TOP_PX || dist < 2;
}

export function ChatView({
  conversationCapabilities,
  selectedContact,
  workspaceTopicView,
  vaultTopicView,
  fetchE2eEncryptedBlob,
  presenceStatus,
  messages,
  chatHasMoreOlder,
  conversationLoading,
  loadingOlder,
  onLoadOlder,
  currentUserId,
  currentUsername,
  currentUserAvatarUrl = null,
  currentUserAvatarUrls = null,
  chatInput,
  chatAttachmentDraft,
  chatReplyTo,
  onReplyToMessage,
  onForwardMessage,
  onToggleReaction,
  onCancelReply,
  isMobile,
  voiceCallEnabled,
  onVoiceCall,
  onBack,
  onChatInput,
  onPickImage,
  onPickFile,
  onRemoveImage,
  onSend,
  onSendVoice,
  onSelectWorkspaceSearchResult,
  requestedJumpMessageId,
  onJumpHandled,
  onWorkspaceTopicDeleted,
  onWorkspaceTopicRenamed,
  workspaceReactionsByMessageId = {},
  sharedTopicPinned = false,
  onToggleSharedTopicPin,
}: ChatViewProps) {
  const effectiveContact = useMemo((): ContactRow | undefined => {
    if (selectedContact) return selectedContact;
    if (!vaultTopicView) return undefined;
    return {
      user_id: currentUserId,
      username: vaultTopicView.vaultName,
      public_key_ed: "",
      public_key_x: "",
    } as ContactRow;
  }, [selectedContact, vaultTopicView, currentUserId]);

  const { supportsComposerAttachments, supportsMessageActions } = conversationCapabilities;
  const { chatState, deleteMessage, retryFailedOutboxSend, dispatchChat } = useChat();
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const newestSentinelRef = useRef<HTMLDivElement | null>(null);
  const oldestSentinelRef = useRef<HTMLDivElement | null>(null);
  /** Пользователь видит край с новыми сообщениями (IntersectionObserver на sentinel). */
  const stickToBottomRef = useRef(true);
  const docFileRef = useRef<HTMLInputElement | null>(null);
  const imageFileRef = useRef<HTMLInputElement | null>(null);
  const attachWrapRef = useRef<HTMLDivElement | null>(null);
  const chatInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const prevConversationScrollIdRef = useRef<string | undefined>(undefined);
  const lastMessageIdRef = useRef<string | undefined>(undefined);
  const pendingScrollRestoreRef = useRef<{ prevHeight: number; prevTop: number } | null>(null);
  const loadOlderLatchRef = useRef(false);
  const onLoadOlderRef = useRef(onLoadOlder);
  onLoadOlderRef.current = onLoadOlder;
  const pinBottomRafRef = useRef(0);
  const msgRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [flashMessageId, setFlashMessageId] = useState<string | null>(null);
  const [menuMessageId, setMenuMessageId] = useState<string | null>(null);
  const [chatInputWidth, setChatInputWidth] = useState(0);
  const [workspaceMembersOpen, setWorkspaceMembersOpen] = useState(false);
  const [workspaceSearchOpen, setWorkspaceSearchOpen] = useState(false);
  const [pinnedMessagesPanelOpen, setPinnedMessagesPanelOpen] = useState(false);
  const [pinnedJumpMessageId, setPinnedJumpMessageId] = useState<string | null>(null);
  const [pinToast, setPinToast] = useState<string | null>(null);
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMemberItem[]>([]);
  const chatUpload = chatState.chatUpload;
  const uploadBusy = chatUpload !== null;
  const canSend = (chatInput.trim().length > 0 || chatAttachmentDraft !== null) && !uploadBusy;
  const showMicButton = !canSend && !uploadBusy && !!onSendVoice;
  const { contactsState, refreshContacts, notifyContactsChanged } = useContacts();
  const { visualMode } = useTheme();
  const { deleteVault: deleteVaultFromContext, renameVault: renameVaultFromContext } = useVault();
  const { isPinned, togglePin } = usePins();

  const conversationScrollId = useMemo(() => {
    if (selectedContact?.user_id) return selectedContact.user_id;
    if (workspaceTopicView) return `${workspaceTopicView.workspaceId}:${workspaceTopicView.topicId}`;
    if (vaultTopicView) return `vault:${vaultTopicView.vaultId}`;
    return null;
  }, [selectedContact?.user_id, workspaceTopicView, vaultTopicView]);
  /** Desktop chat column wider than 860px: left-aligned thread + rail; narrower = classic L/R bubbles (DM). */
  const isWideDesktopThreadLayout = !isMobile && chatInputWidth > 0 && chatInputWidth > 860;
  const uploadPercent =
    chatUpload?.phase === "uploading" && chatUpload.total && chatUpload.total > 0
      ? Math.max(0, Math.min(100, Math.round((chatUpload.loaded / chatUpload.total) * 100)))
      : null;
  const uploadDetail =
    chatUpload?.phase === "uploading"
      ? [formatUploadBytes(chatUpload.loaded), formatUploadBytes(chatUpload.total)].filter(Boolean).join(" / ")
      : null;

  useEffect(() => {
    setWorkspaceMembersOpen(false);
  }, [workspaceTopicView?.workspaceId]);

  useEffect(() => {
    setWorkspaceSearchOpen(false);
  }, [workspaceTopicView?.workspaceId, workspaceTopicView?.topicId]);

  useEffect(() => {
    setPinnedMessagesPanelOpen(false);
    setPinnedJumpMessageId(null);
    setPinToast(null);
  }, [conversationScrollId]);

  useEffect(() => {
    if (uploadBusy) setAttachMenuOpen(false);
  }, [uploadBusy]);

  useEffect(() => {
    if (!pinToast) return;
    const t = window.setTimeout(() => setPinToast(null), 1800);
    return () => window.clearTimeout(t);
  }, [pinToast]);

  useEffect(() => {
    if (!workspaceTopicView) {
      setWorkspaceMembers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const { members } = await apiFetchWorkspaceMembers(workspaceTopicView.workspaceId);
        if (!cancelled) setWorkspaceMembers(members);
      } catch {
        if (!cancelled) setWorkspaceMembers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceTopicView?.workspaceId]);

  useLayoutEffect(() => {
    const input = chatInputRef.current;
    if (!input) return;
    const updateWidth = () => {
      const next = input.clientWidth;
      setChatInputWidth((current) => (current === next ? current : next));
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(input);
    return () => ro.disconnect();
  }, [conversationScrollId, workspaceTopicView ? "workspace" : vaultTopicView ? "vault" : "dm"]);

  /** Авто-высота textarea (многострочный ввод). Не ниже touch-min — вровень с кнопками вложения/отправки. */
  useLayoutEffect(() => {
    const ta = chatInputRef.current;
    if (!ta) return;
    const minH = 44;
    ta.style.height = "0";
    ta.style.height = `${Math.min(Math.max(ta.scrollHeight, minH), 200)}px`;
  }, [chatInput, conversationScrollId]);

  /** Десктоп (широкая колонка): Enter — отправка; Shift+Enter — перенос. Узкий экран / «мобильный»: Enter — перенос, отправка — только кнопка. */
  const onComposerKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLTextAreaElement>, canSendFlag: boolean) => {
      if (e.key !== "Enter" || e.shiftKey) return;
      if (e.nativeEvent.isComposing) return;
      if (isMobile) return;
      e.preventDefault();
      if (canSendFlag) onSend();
    },
    [isMobile, onSend],
  );

  const contactsMap = useMemo(() => new Map(contactsState.contacts.map((row) => [row.user_id, row])), [contactsState.contacts]);
  const workspaceMembersMap = useMemo(
    () => new Map(workspaceMembers.map((row) => [row.user_id, row])),
    [workspaceMembers],
  );

  const currentUserAvatarSources = useMemo(() => {
    if (currentUserAvatarUrls) return avatarUrlsPrimaryFallback(visualMode, currentUserAvatarUrls);
    if (currentUserAvatarUrl) {
      return avatarUrlsPrimaryFallback(visualMode, {
        original_url: currentUserAvatarUrl,
        terminal_url: currentUserAvatarUrl,
      });
    }
    return { primary: null, fallback: null };
  }, [visualMode, currentUserAvatarUrls, currentUserAvatarUrl]);

  const selectedPeerAvatarSources = useMemo(() => {
    if (vaultTopicView && !selectedContact) return currentUserAvatarSources;
    if (!selectedContact) return { primary: null, fallback: null };
    return peerAvatarImageSources(visualMode, selectedContact);
  }, [visualMode, selectedContact, vaultTopicView, currentUserAvatarSources]);

  const workspaceSelfRole = useMemo((): "owner" | "admin" | "member" | null => {
    if (!workspaceTopicView) return null;
    return workspaceMembersMap.get(currentUserId)?.role ?? "member";
  }, [workspaceTopicView, workspaceMembersMap, currentUserId]);

  /** Prefer loaded members row; fall back to shell-provided role while members fetch. */
  const effectiveWorkspaceRoleForUi = useMemo((): "owner" | "admin" | "member" => {
    if (!workspaceTopicView) return "member";
    const fromMembers = workspaceMembersMap.get(currentUserId)?.role;
    if (fromMembers) return fromMembers;
    const r = workspaceTopicView.workspaceRole;
    if (r === "owner" || r === "admin" || r === "member") return r;
    return "member";
  }, [workspaceTopicView, workspaceMembersMap, currentUserId]);

  const handleRemoveDmContact = useCallback(async () => {
    if (!selectedContact || vaultTopicView) return;
    if (!window.confirm(`Удалить @${selectedContact.username} из контактов?`)) return;
    try {
      await apiRemoveContact(selectedContact.user_id);
      await removeContactLocal(selectedContact.user_id);
      notifyContactsChanged();
      await refreshContacts();
      dispatchChat({ type: "set-selected-conversation", conversation: null });
    } catch (e: unknown) {
      dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
    }
  }, [selectedContact, vaultTopicView, notifyContactsChanged, refreshContacts, dispatchChat]);

  const handleDeleteVaultChat = useCallback(async () => {
    if (!vaultTopicView) return;
    if (!window.confirm(`Удалить хранилище «${vaultTopicView.vaultName}»?`)) return;
    try {
      await deleteVaultFromContext(vaultTopicView.vaultId);
      dispatchChat({ type: "set-selected-conversation", conversation: null });
    } catch (e: unknown) {
      dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
    }
  }, [vaultTopicView, deleteVaultFromContext, dispatchChat]);

  const handleDeleteWorkspaceTopic = useCallback(async () => {
    if (!workspaceTopicView) return;
    if (workspaceTopicView.topicName === "general") {
      dispatchChat({ type: "set-error", error: "Нельзя удалить тему general" });
      return;
    }
    if (effectiveWorkspaceRoleForUi !== "owner" && effectiveWorkspaceRoleForUi !== "admin") return;
    if (!window.confirm(`Удалить тему «${workspaceTopicView.topicName}»?`)) return;
    try {
      await apiDeleteWorkspaceTopic(workspaceTopicView.workspaceId, workspaceTopicView.topicId);
      onWorkspaceTopicDeleted?.();
    } catch (e: unknown) {
      dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
    }
  }, [workspaceTopicView, effectiveWorkspaceRoleForUi, onWorkspaceTopicDeleted, dispatchChat]);

  const handleRenameWorkspaceTopic = useCallback(async () => {
    if (!workspaceTopicView) return;
    if (workspaceTopicView.topicName === "general") {
      dispatchChat({ type: "set-error", error: "Нельзя переименовать тему general" });
      return;
    }
    if (effectiveWorkspaceRoleForUi !== "owner" && effectiveWorkspaceRoleForUi !== "admin") return;
    const next = window.prompt("Новое имя темы:", workspaceTopicView.topicName)?.trim();
    if (!next || next === workspaceTopicView.topicName) return;
    try {
      await apiRenameWorkspaceTopic(workspaceTopicView.workspaceId, workspaceTopicView.topicId, next);
      onWorkspaceTopicRenamed?.(next);
    } catch (e: unknown) {
      dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
    }
  }, [workspaceTopicView, effectiveWorkspaceRoleForUi, onWorkspaceTopicRenamed, dispatchChat]);

  const handleRenameVaultChat = useCallback(async () => {
    if (!vaultTopicView) return;
    const next = window.prompt("Новое имя хранилища:", vaultTopicView.vaultName)?.trim();
    if (!next || next === vaultTopicView.vaultName) return;
    try {
      await renameVaultFromContext(vaultTopicView.vaultId, next);
    } catch (e: unknown) {
      dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
    }
  }, [vaultTopicView, renameVaultFromContext, dispatchChat]);

  const workspaceTopicOverflowItems = useMemo((): MobileEntityHeaderMenuItem[] => {
    if (!workspaceTopicView) return [];
    const items: MobileEntityHeaderMenuItem[] = [];
    items.push(
      {
        id: "search",
        label: "Поиск по workspace",
        onSelect: () => {
          setPinnedMessagesPanelOpen(false);
          setWorkspaceSearchOpen((v) => !v);
        },
      },
      {
        id: "members",
        label: "Участники workspace",
        onSelect: () => setWorkspaceMembersOpen(true),
      },
    );
    if (effectiveWorkspaceRoleForUi === "owner" || effectiveWorkspaceRoleForUi === "admin") {
      if (onToggleSharedTopicPin) {
        items.push({
          id: "pin-topic",
          label: sharedTopicPinned ? "Открепить тему" : "Закрепить тему",
          onSelect: () => onToggleSharedTopicPin(),
        });
      }
      items.push({
        id: "rename-topic",
        label: "Переименовать тему",
        onSelect: () => void handleRenameWorkspaceTopic(),
      });
      items.push({
        id: "del-topic",
        label: "Удалить тему",
        danger: true,
        onSelect: () => void handleDeleteWorkspaceTopic(),
      });
    }
    return items;
  }, [
    workspaceTopicView,
    effectiveWorkspaceRoleForUi,
    sharedTopicPinned,
    onToggleSharedTopicPin,
    handleRenameWorkspaceTopic,
    handleDeleteWorkspaceTopic,
  ]);

  const dmOrVaultOverflowItems = useMemo((): MobileEntityHeaderMenuItem[] => {
    if (vaultTopicView) {
      const vaultItems: MobileEntityHeaderMenuItem[] = [];
      vaultItems.push(
        {
          id: "pin-vault",
          label: isPinned("vault", vaultTopicView.vaultId) ? "Открепить хранилище" : "Закрепить хранилище",
          onSelect: () => togglePin("vault", vaultTopicView.vaultId),
        },
        {
          id: "rename-vault",
          label: "Переименовать хранилище",
          onSelect: () => void handleRenameVaultChat(),
        },
        {
          id: "del-vault",
          label: "Удалить хранилище",
          danger: true,
          onSelect: () => void handleDeleteVaultChat(),
        },
      );
      return vaultItems;
    }
    const peerId = selectedContact?.user_id;
    const items: MobileEntityHeaderMenuItem[] = [];
    if (peerId) {
      items.push({
        id: "pin-contact",
        label: isPinned("contact", peerId) ? "Открепить контакт" : "Закрепить контакт",
        onSelect: () => togglePin("contact", peerId),
      });
    }
    items.push({
      id: "remove-contact",
      label: "Удалить из контактов",
      danger: true,
      onSelect: () => void handleRemoveDmContact(),
    });
    return items;
  }, [
    vaultTopicView,
    selectedContact?.user_id,
    isPinned,
    togglePin,
    handleRenameVaultChat,
    handleDeleteVaultChat,
    handleRemoveDmContact,
  ]);

  const canDeleteMessage = useCallback(
    (message: MessageRow) => {
      if (isTombstonePlaintext(message.plaintext) || message.deleted) return false;
      if (vaultTopicView) return message.from_id === currentUserId;
      if (!workspaceTopicView) return message.from_id === currentUserId;
      if (message.from_id === currentUserId) return true;
      return workspaceSelfRole === "owner" || workspaceSelfRole === "admin";
    },
    [workspaceTopicView, vaultTopicView, currentUserId, workspaceSelfRole],
  );

  const resolveRemoteAuthor = useCallback(
    (fromId: string) => {
      const workspaceMember = workspaceMembersMap.get(fromId);
      if (workspaceMember) return workspaceMember.username;
      const contact = contactsMap.get(fromId);
      if (contact) return contact.username;
      return `${fromId.slice(0, 8)}…`;
    },
    [contactsMap, workspaceMembersMap],
  );

  const resolveRemoteAuthorAvatarSources = useCallback(
    (fromId: string) => {
      const workspaceMember = workspaceMembersMap.get(fromId);
      if (workspaceMember) return peerAvatarImageSources(visualMode, workspaceMember);
      const contact = contactsMap.get(fromId);
      if (contact) return peerAvatarImageSources(visualMode, contact);
      return { primary: null, fallback: null };
    },
    [contactsMap, workspaceMembersMap, visualMode],
  );

  const handleComposerPaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (!supportsComposerAttachments) return;
      const cd = e.clipboardData;
      if (!cd) return;
      for (let i = 0; i < cd.items.length; i++) {
        const item = cd.items[i];
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            onPickImage(file);
            return;
          }
        }
      }
      const { files } = cd;
      if (files && files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          const f = files[i];
          if (f.type.startsWith("image/")) {
            e.preventDefault();
            onPickImage(f);
            return;
          }
        }
      }
    },
    [supportsComposerAttachments, onPickImage],
  );


  const pinnedMessageIds = useMemo(
    () => new Set(chatState.pinnedMessages.map((p) => p.message_id)),
    [chatState.pinnedMessages],
  );

  const pinnedMessagesForPanel = useMemo(
    () => {
      const loadedPreviewById = new Map(
        messages.map((message) => [message.id, previewTextForReply(message.plaintext)]),
      );
      return chatState.pinnedMessages.map((pin) => {
        const loadedPreview = loadedPreviewById.get(pin.message_id);
        if (loadedPreview) return { ...pin, body_preview: loadedPreview };
        if (pin.body_preview && !pin.body_preview.startsWith("enc:v1:")) return pin;
        return { ...pin, body_preview: "Сообщение" };
      });
    },
    [chatState.pinnedMessages, messages],
  );

  const handleTogglePinMessage = useCallback(
    async (message: MessageRow) => {
      if (!supportsMessageActions) return;
      const conv =
        workspaceTopicView != null
          ? {
              kind: "workspace-topic" as const,
              workspaceId: workspaceTopicView.workspaceId,
              topicId: workspaceTopicView.topicId,
            }
          : vaultTopicView != null
            ? { kind: "vault" as const, vaultId: vaultTopicView.vaultId }
            : selectedContact != null
              ? { kind: "dm" as const, peerId: selectedContact.user_id }
              : null;
      if (!conv) return;
      const { contextType, contextId } = buildPinnedMessageContextId(conv, currentUserId);
      const pinned = pinnedMessageIds.has(message.id);
      try {
        if (pinned) {
          await apiUnpinMessage(contextType, contextId, message.id);
        } else {
          await apiPinMessage(contextType, contextId, message.id);
        }
        const pins = await apiFetchPinnedMessages(contextType, contextId);
        dispatchChat({ type: "set-pinned-messages", pins });
        setPinToast(pinned ? "Сообщение откреплено" : "Сообщение закреплено");
      } catch (e: unknown) {
        if (pinned) {
          dispatchChat({ type: "remove-pinned-message", messageId: message.id });
        } else {
          const authorUsername = workspaceTopicView
            ? message.from_id === currentUserId
              ? currentUsername
              : resolveRemoteAuthor(message.from_id)
            : vaultTopicView
              ? currentUsername
              : message.from_id === currentUserId
                ? currentUsername
                : effectiveContact?.username ?? null;
          dispatchChat({
            type: "add-pinned-message",
            item: {
              message_id: message.id,
              pinned_by: currentUserId,
              pinned_at: Date.now(),
              created_at: message.created_at,
              body_preview: previewTextForReply(message.plaintext),
              author_username: authorUsername,
              from_id: message.from_id,
            },
          });
        }
        dispatchChat({ type: "set-error", error: e instanceof Error ? e.message : String(e) });
      }
    },
    [
      supportsMessageActions,
      workspaceTopicView,
      vaultTopicView,
      selectedContact,
      currentUserId,
      currentUsername,
      effectiveContact?.username,
      resolveRemoteAuthor,
      pinnedMessageIds,
      dispatchChat,
    ],
  );

  const resolveRemoteAuthorRole = useCallback(
    (fromId: string): string | undefined => {
      return workspaceMembersMap.get(fromId)?.role;
    },
    [workspaceMembersMap],
  );

  const { visibleMessages, reactionsByMessageId: dmReactionsByMessageId } = useMemo(() => {
    const visible = messages.filter((message) => envelopeReactionMeta(message.plaintext) === null);
    const visibleIds = new Set(visible.map((message) => message.id));
    const latestByActorEmoji = new Map<string, { active: boolean; from_id: string; targetId: string; emoji: string }>();

    for (const message of messages) {
      const reaction = envelopeReactionMeta(message.plaintext);
      if (!reaction || !visibleIds.has(reaction.target_msg_id)) continue;
      latestByActorEmoji.set(`${reaction.target_msg_id}:${message.from_id}:${reaction.emoji}`, {
        active: reaction.action === "add",
        from_id: message.from_id,
        targetId: reaction.target_msg_id,
        emoji: reaction.emoji,
      });
    }

    const byMessageId = new Map<string, Map<string, MessageReactionBadge>>();
    for (const reaction of latestByActorEmoji.values()) {
      if (!reaction.active) continue;
      const bucket = byMessageId.get(reaction.targetId) ?? new Map<string, MessageReactionBadge>();
      const badge = bucket.get(reaction.emoji) ?? {
        emoji: reaction.emoji,
        count: 0,
        ownSelected: false,
      };
      badge.count += 1;
      badge.ownSelected = badge.ownSelected || reaction.from_id === currentUserId;
      bucket.set(reaction.emoji, badge);
      byMessageId.set(reaction.targetId, bucket);
    }

    const reactions = Object.fromEntries(
      [...byMessageId.entries()].map(([messageId, badges]) => [
        messageId,
        [...badges.values()].sort((a, b) => {
          if (a.ownSelected !== b.ownSelected) return a.ownSelected ? -1 : 1;
          if (a.count !== b.count) return b.count - a.count;
          return a.emoji.localeCompare(b.emoji);
        }),
      ]),
    ) as Record<string, MessageReactionBadge[]>;

    return {
      visibleMessages: visible,
      reactionsByMessageId: reactions,
    };
  }, [messages, currentUserId]);

  const reactionsByMessageId = useMemo(() => {
    if (!workspaceTopicView) return dmReactionsByMessageId;
    const merged: Record<string, MessageReactionBadge[]> = { ...dmReactionsByMessageId };
    for (const [id, badges] of Object.entries(workspaceReactionsByMessageId)) {
      merged[id] = badges;
    }
    return merged;
  }, [workspaceTopicView, dmReactionsByMessageId, workspaceReactionsByMessageId]);

  const visibleNewestFirst = useMemo(() => [...visibleMessages].reverse(), [visibleMessages]);

  const bubbleRowObserveKey = useMemo(
    () => visibleMessages.map((m) => m.id).join("\0"),
    [visibleMessages],
  );

  const scrollToMessage = useCallback((messageId: string) => {
    const el = msgRefs.current[messageId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "nearest" });
      setFlashMessageId(messageId);
      window.setTimeout(() => setFlashMessageId(null), 1500);
    }
  }, []);

  const jumpTarget = requestedJumpMessageId ?? pinnedJumpMessageId;
  useEffect(() => {
    if (!jumpTarget) return;
    if (!visibleMessages.some((message) => message.id === jumpTarget)) return;
    const raf = window.requestAnimationFrame(() => {
      scrollToMessage(jumpTarget);
      if (requestedJumpMessageId === jumpTarget) {
        onJumpHandled?.(jumpTarget);
      }
      if (pinnedJumpMessageId === jumpTarget) {
        setPinnedJumpMessageId(null);
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [
    onJumpHandled,
    requestedJumpMessageId,
    pinnedJumpMessageId,
    jumpTarget,
    scrollToMessage,
    visibleMessages,
  ]);

  useEffect(() => {
    if (!pinnedJumpMessageId) return;
    if (messages.some((m) => m.id === pinnedJumpMessageId)) return;
    if (!chatHasMoreOlder || loadingOlder) return;
    void onLoadOlder();
  }, [pinnedJumpMessageId, messages, chatHasMoreOlder, loadingOlder, onLoadOlder]);

  useEffect(() => {
    const body = bodyRef.current;
    const sentinel = newestSentinelRef.current;
    if (!body || !sentinel || !conversationScrollId || visibleMessages.length === 0) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        stickToBottomRef.current = Boolean(e?.isIntersecting);
      },
      { root: body, rootMargin: "0px", threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [conversationScrollId, bubbleRowObserveKey, visibleMessages.length]);

  const tryTriggerLoadOlder = useCallback(() => {
    const el = bodyRef.current;
    if (!el || !chatHasMoreOlder || loadingOlder || loadOlderLatchRef.current) return;
    loadOlderLatchRef.current = true;
    pendingScrollRestoreRef.current = { prevHeight: el.scrollHeight, prevTop: el.scrollTop };
    onLoadOlderRef.current();
  }, [chatHasMoreOlder, loadingOlder]);

  useEffect(() => {
    const body = bodyRef.current;
    const sen = oldestSentinelRef.current;
    if (!body || !sen || !conversationScrollId || visibleMessages.length === 0 || !chatHasMoreOlder) {
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (!e?.isIntersecting) return;
        tryTriggerLoadOlder();
      },
      {
        root: body,
        rootMargin: "400px 0px 200px 0px",
        threshold: 0,
      },
    );
    io.observe(sen);
    return () => io.disconnect();
  }, [chatHasMoreOlder, conversationScrollId, bubbleRowObserveKey, visibleMessages.length, tryTriggerLoadOlder]);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (pendingScrollRestoreRef.current && loadingOlder) {
      return;
    }
    if (pendingScrollRestoreRef.current && body && !loadingOlder) {
      const { prevHeight, prevTop } = pendingScrollRestoreRef.current;
      pendingScrollRestoreRef.current = null;
      const newHeight = body.scrollHeight;
      body.scrollTop = newHeight - prevHeight + prevTop;
      lastMessageIdRef.current = lastNonReactionTailId(messages);
      return;
    }

    const convId = conversationScrollId;
    if (!convId) return;

    if (prevConversationScrollIdRef.current !== convId) {
      pendingScrollRestoreRef.current = null;
      prevConversationScrollIdRef.current = convId;
      lastMessageIdRef.current = undefined;
      stickToBottomRef.current = true;
      return;
    }

    if (messages.length === 0) {
      lastMessageIdRef.current = undefined;
      return;
    }

    const lastChatTailId = lastNonReactionTailId(messages);
    const prevLastChatTailId = lastMessageIdRef.current;

    const atNewestViewport =
      stickToBottomRef.current || (body !== null && body.scrollTop <= NEAR_BOTTOM_PX);
    if (lastChatTailId !== prevLastChatTailId && body && atNewestViewport) {
      const lastMsg = messages.find((m) => m.id === lastChatTailId);
      const lastFromSelf = lastMsg?.from_id === currentUserId;
      const typingInComposer =
        chatInputRef.current !== null &&
        document.activeElement === chatInputRef.current;
      if (!(typingInComposer && !lastFromSelf)) {
        body.scrollTop = 0;
      }
    }
    lastMessageIdRef.current = lastChatTailId;
  }, [messages, conversationScrollId, loadingOlder, currentUserId]);

  /** Картинки и др. меняют высоту строки после открытия чата — поджимаем к низу, только если пользователь у низа. */
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body || !conversationScrollId) return;

    const schedulePinToBottom = () => {
      if (!stickToBottomRef.current) return;
      if (pinBottomRafRef.current) return;
      pinBottomRafRef.current = requestAnimationFrame(() => {
        pinBottomRafRef.current = 0;
        if (!stickToBottomRef.current) return;
        const el = bodyRef.current;
        if (el) el.scrollTop = 0;
      });
    };

    const ro = new ResizeObserver(schedulePinToBottom);
    for (const row of body.querySelectorAll(".bubble-row")) {
      ro.observe(row);
    }
    return () => {
      ro.disconnect();
      if (pinBottomRafRef.current) {
        cancelAnimationFrame(pinBottomRafRef.current);
        pinBottomRafRef.current = 0;
      }
    };
  }, [bubbleRowObserveKey, conversationScrollId]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !conversationScrollId) return;
    const onLoadCapture = (e: Event) => {
      if (!(e.target instanceof HTMLImageElement)) return;
      if (!body.contains(e.target)) return;
      if (!stickToBottomRef.current) return;
      body.scrollTop = 0;
    };
    body.addEventListener("load", onLoadCapture, true);
    return () => body.removeEventListener("load", onLoadCapture, true);
  }, [conversationScrollId]);

  useEffect(() => {
    if (!loadingOlder) {
      loadOlderLatchRef.current = false;
    }
  }, [loadingOlder]);

  useEffect(() => {
    if (chatReplyTo) {
      chatInputRef.current?.focus();
    }
  }, [chatReplyTo]);

  useEffect(() => {
    if (menuMessageId && !visibleMessages.some((message) => message.id === menuMessageId)) {
      setMenuMessageId(null);
    }
  }, [menuMessageId, visibleMessages]);

  /** Закрыть action-menu по тапу вне пузыря / панели эмодзи (фон, шапка, композер и т.д.). */
  useEffect(() => {
    if (menuMessageId === null) return;
    const onPointerDownCapture = (e: PointerEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      if (t.closest(".bubble__actions")) return;
      // Строка на всю ширину; клик по полосе рядом с пузырём — всё ещё bubble-row, но не .bubble.
      const bubble = t.closest(".bubble");
      if (bubble) {
        const row = bubble.closest(".bubble-row[data-message-id]");
        const rowId = row?.getAttribute("data-message-id");
        if (rowId === menuMessageId) return;
      }
      setMenuMessageId(null);
    };
    document.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => document.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [menuMessageId]);

  /** Скролл ленты — закрыть меню только при заметном сдвиге (избегаем моргания от layout/программного скролла). */
  useEffect(() => {
    if (menuMessageId === null) return;
    const body = bodyRef.current;
    if (!body) return;
    const scrollTopWhenOpened = body.scrollTop;
    const onScroll = () => {
      if (Math.abs(body.scrollTop - scrollTopWhenOpened) >= MENU_SCROLL_CLOSE_PX) {
        setMenuMessageId(null);
      }
    };
    body.addEventListener("scroll", onScroll, { passive: true });
    return () => body.removeEventListener("scroll", onScroll);
  }, [menuMessageId]);

  useEffect(() => {
    setAttachMenuOpen(false);
  }, [conversationScrollId]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (attachWrapRef.current?.contains(t)) return;
      setAttachMenuOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [attachMenuOpen]);

  useEffect(() => {
    if (!attachMenuOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setAttachMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [attachMenuOpen]);

  const onBodyScroll = () => {
    const el = bodyRef.current;
    if (!el || !isNearOldestEdge(el)) return;
    tryTriggerLoadOlder();
  };

  const renderComposerAttachControls = () => (
    <>
      <input
        ref={docFileRef}
        type="file"
        className="chat-shell__file"
        aria-hidden="true"
        tabIndex={-1}
        disabled={uploadBusy}
        onChange={(e) => {
          onPickFile(e.target.files?.[0] ?? null);
          e.target.value = "";
          setAttachMenuOpen(false);
        }}
      />
      <input
        ref={imageFileRef}
        type="file"
        accept="image/*"
        className="chat-shell__file"
        aria-hidden="true"
        tabIndex={-1}
        disabled={uploadBusy}
        onChange={(e) => {
          onPickImage(e.target.files?.[0] ?? null);
          e.target.value = "";
          setAttachMenuOpen(false);
        }}
      />
      <div ref={attachWrapRef} className="chat-shell__attach-wrap">
        {attachMenuOpen ? (
          <div className="attach-menu" role="menu">
            <button
              type="button"
              className="attach-menu__btn"
              role="menuitem"
              disabled={uploadBusy}
              onClick={() => {
                setAttachMenuOpen(false);
                imageFileRef.current?.click();
              }}
            >
              <AppIcon name="image" size={20} className="attach-menu__icon" />
              <span className="attach-menu__label">Фото</span>
            </button>
            <button
              type="button"
              className="attach-menu__btn"
              role="menuitem"
              disabled={uploadBusy}
              onClick={() => {
                setAttachMenuOpen(false);
                docFileRef.current?.click();
              }}
            >
              <AppIcon name="paperclip" size={20} className="attach-menu__icon" />
              <span className="attach-menu__label">Файл</span>
            </button>
          </div>
        ) : null}
        <button
          type="button"
          className="chat-shell__attach"
          aria-label="Прикрепить"
          aria-expanded={attachMenuOpen}
          aria-haspopup="menu"
          title="Фото или файл"
          disabled={uploadBusy}
          onClick={() => setAttachMenuOpen((o) => !o)}
        >
          <AppIcon name="paperclip" className="chat-shell__attach-icon" />
        </button>
      </div>
    </>
  );

  const renderUploadStatus = (compact = false) => {
    if (!chatUpload) return null;
    return (
      <div
        className={`chat-shell__upload${compact ? " chat-shell__upload--compact" : ""}`}
        role="status"
        aria-live="polite"
      >
        <div className="chat-shell__upload-head">
          <span className="chat-shell__upload-label">{chatUpload.label}</span>
          <span className="chat-shell__upload-meta">
            {chatUpload.phase === "preparing"
              ? "шифруем..."
              : chatUpload.phase === "processing"
                ? "сохраняем на сервере..."
                : uploadPercent != null
                  ? `${uploadPercent}%`
                  : "отправляем..."}
          </span>
        </div>
        {chatUpload.phase === "uploading" ? (
          <>
            <div className="chat-shell__upload-bar" aria-hidden="true">
              <span
                className="chat-shell__upload-bar-fill"
                style={{ width: `${uploadPercent ?? 12}%` }}
              />
            </div>
            {uploadDetail ? <div className="chat-shell__upload-detail">{uploadDetail}</div> : null}
          </>
        ) : (
          <div className="chat-shell__upload-bar chat-shell__upload-bar--indeterminate" aria-hidden="true">
            <span className="chat-shell__upload-bar-fill chat-shell__upload-bar-fill--indeterminate" />
          </div>
        )}
      </div>
    );
  };

  const renderComposerDraft = () => {
    if (!(supportsComposerAttachments && chatAttachmentDraft)) return null;
    return (
      <div className={`chat-shell__draft${uploadBusy ? " chat-shell__draft--busy" : ""}`}>
        <div className="chat-shell__draft-main">
          {chatAttachmentDraft.kind === "image" ? (
            <img src={chatAttachmentDraft.previewUrl} alt="" className="chat-shell__draft-img" />
          ) : (
            <span className="chat-shell__draft-file">{chatAttachmentDraft.filename}</span>
          )}
          {uploadBusy ? renderUploadStatus(true) : null}
        </div>
        <button
          type="button"
          className="chat-shell__draft-remove"
          onClick={onRemoveImage}
          aria-label="Убрать вложение"
          disabled={uploadBusy}
        >
          ×
        </button>
      </div>
    );
  };

  if (conversationLoading && !selectedContact && !workspaceTopicView && !vaultTopicView) {
    return <div className="chat-shell chat-shell--empty">{renderConversationLoadingState()}</div>;
  }

  if (!effectiveContact && !workspaceTopicView) {
    return (
      <div className="chat-shell chat-shell--empty">
        <div className="chat-empty">
          <span className="chat-empty__hint">// выберите контакт</span>
        </div>
      </div>
    );
  }

  if (workspaceTopicView && !selectedContact) {
    const {
      supportsTopics,
      supportsRoles,
      supportsReadReceipts,
    } = conversationCapabilities;
    const canSendWs = (chatInput.trim().length > 0 || chatAttachmentDraft !== null) && !uploadBusy;
    const showMicWs = !canSendWs && !uploadBusy && !!onSendVoice;
    return (
      <div className="chat-shell chat-shell--workspace-topic">
        {isMobile ? (
          <MobileEntityHeader
            onBack={onBack}
            title={`#${truncate(workspaceTopicView.topicName, 18)}`}
            titleTitle={workspaceTopicView.topicName}
            subtitle={truncate(workspaceTopicView.workspaceName, 32)}
            overflowItems={workspaceTopicOverflowItems}
            onPinnedMessages={
              supportsMessageActions ? () => setPinnedMessagesPanelOpen((v) => !v) : undefined
            }
            pinnedCount={chatState.pinnedMessages.length}
            dataSupportsTopics={supportsTopics}
            dataSupportsRoles={supportsRoles}
          />
        ) : (
          <div
            className="chat-shell__head"
            data-supports-topics={supportsTopics ? "1" : "0"}
            data-supports-roles={supportsRoles ? "1" : "0"}
          >
            <div className="chat-shell__topic-titles">
              <span className="chat-shell__contact chat-shell__topic-name" title={workspaceTopicView.topicName}>
                #{truncate(workspaceTopicView.topicName, 14)}
              </span>
              <span
                className="chat-shell__topic-workspace"
                title={workspaceTopicView.workspaceName}
              >
                {truncate(workspaceTopicView.workspaceName, 28)}
              </span>
            </div>
            {effectiveWorkspaceRoleForUi === "owner" || effectiveWorkspaceRoleForUi === "admin" ? (
              onToggleSharedTopicPin ? (
                <button
                  type="button"
                  className="chat-shell__head-icon-btn"
                  title={sharedTopicPinned ? "Открепить тему" : "Закрепить тему"}
                  aria-label={sharedTopicPinned ? "Открепить тему" : "Закрепить тему"}
                  onClick={() => onToggleSharedTopicPin()}
                >
                  <AppIcon name={sharedTopicPinned ? "pin-off" : "pin"} className="chat-shell__head-icon-svg" />
                </button>
              ) : null
            ) : null}
            {supportsMessageActions ? (
              <button
                type="button"
                className="chat-shell__head-icon-btn chat-shell__head-icon-btn--with-badge"
                title="Закреплённые сообщения"
                aria-label="Закреплённые сообщения"
                onClick={() => {
                  setWorkspaceSearchOpen(false);
                  setPinnedMessagesPanelOpen((v) => !v);
                }}
              >
                <AppIcon name="pin" className="chat-shell__head-icon-svg" />
                {chatState.pinnedMessages.length > 0 ? (
                  <span className="chat-shell__head-badge">{chatState.pinnedMessages.length}</span>
                ) : null}
              </button>
            ) : null}
            <button
              type="button"
              className="chat-shell__head-icon-btn"
              title="Поиск по workspace"
              aria-label="Поиск по workspace"
              onClick={() => {
                setPinnedMessagesPanelOpen(false);
                setWorkspaceSearchOpen((v) => !v);
              }}
            >
              <AppIcon name="search" className="chat-shell__head-icon-svg" />
            </button>
            <button
              type="button"
              className="chat-shell__members-btn"
              title="Участники workspace"
              onClick={() => setWorkspaceMembersOpen(true)}
            >
              участники
            </button>
          </div>
        )}
        {pinToast ? (
          <div className="chat-shell__pin-toast" role="status">
            {pinToast}
          </div>
        ) : null}
        {workspaceSearchOpen ? workspaceTopicView && onSelectWorkspaceSearchResult ? (
          <WorkspaceMessageSearchPanel
            workspaceId={workspaceTopicView.workspaceId}
            currentTopicId={workspaceTopicView.topicId}
            onClose={() => setWorkspaceSearchOpen(false)}
            onSelectResult={(args) => {
              setWorkspaceSearchOpen(false);
              onSelectWorkspaceSearchResult(args);
            }}
          />
        ) : null : null}
        {pinnedMessagesPanelOpen && supportsMessageActions ? (
          <PinnedMessagesPanel
            pins={pinnedMessagesForPanel}
            onClose={() => setPinnedMessagesPanelOpen(false)}
            onSelectMessageId={(id) => {
              setPinnedMessagesPanelOpen(false);
              setPinnedJumpMessageId(id);
            }}
          />
        ) : null}
        {isMobile && supportsMessageActions ? (
          <div className="chat-shell__reply-hint" aria-hidden="true">
            тап по сообщению → реакции и reply · свайп → быстрый reply
          </div>
        ) : null}
        <div className="chat-shell__body" ref={bodyRef} onScroll={onBodyScroll}>
          {conversationLoading && messages.length === 0 ? (
            renderConversationLoadingState()
          ) : visibleMessages.length === 0 ? (
            <div className="chat-empty chat-empty--inline">
              <span className="chat-empty__hint">// нет сообщений — напишите первым</span>
            </div>
          ) : (
            <>
              <div ref={newestSentinelRef} className="chat-shell__newest-sentinel" aria-hidden />
              {visibleNewestFirst.map((message, index) => {
                const rid = envelopeReplyToId(message.plaintext);
                const replyParent =
                  rid === null ? undefined : (messages.find((m) => m.id === rid) ?? null);
                const newerMessage = index > 0 ? visibleNewestFirst[index - 1] : undefined;
                const olderMessage = index < visibleNewestFirst.length - 1 ? visibleNewestFirst[index + 1] : undefined;
                const isRemote = message.from_id !== currentUserId;
                const showAuthorName = isRemote && olderMessage?.from_id !== message.from_id;
                const showAuthorAvatar = isRemote
                  ? true
                  : isWideDesktopThreadLayout && newerMessage?.from_id !== message.from_id;
                const railLead = isRemote
                  ? resolveRemoteAuthorAvatarSources(message.from_id)
                  : currentUserAvatarSources;
                return (
                  <div
                    key={message.id}
                    ref={(el) => {
                      msgRefs.current[message.id] = el;
                    }}
                    className={`bubble-row ${isWideDesktopThreadLayout ? "bubble-row--compact" : ""}`}
                    data-message-id={message.id}
                  >
                    <MessageBubble
                      message={message}
                      ownUserId={currentUserId}
                      ownUsername={currentUsername}
                      peerUsername={workspaceTopicView.topicName}
                      supportsReadReceipts={supportsReadReceipts}
                      plaintext={message.plaintext}
                      replyParent={replyParent}
                      isHighlighted={flashMessageId === message.id}
                      isMobile={isMobile}
                      reactions={reactionsByMessageId[message.id] ?? []}
                      isMenuOpen={supportsMessageActions ? menuMessageId === message.id : false}
                      onToggleMenu={() =>
                        setMenuMessageId((current) => (current === message.id ? null : message.id))
                      }
                      onCloseMenu={() =>
                        setMenuMessageId((current) => (current === message.id ? null : current))
                      }
                      onReply={onReplyToMessage}
                      onForward={onForwardMessage}
                      onToggleReaction={onToggleReaction}
                      onScrollToMessage={scrollToMessage}
                      supportsMessageActions={supportsMessageActions}
                      isMessagePinned={pinnedMessageIds.has(message.id)}
                      onTogglePinMessage={supportsMessageActions ? handleTogglePinMessage : undefined}
                      canDeleteMessage={canDeleteMessage(message)}
                      onDeleteMessage={() => void deleteMessage(message)}
                      onRetryFailedOutbox={(m) => void retryFailedOutboxSend(m.id)}
                      resolveRemoteAuthor={resolveRemoteAuthor}
                      resolveRemoteAuthorAvatarSources={resolveRemoteAuthorAvatarSources}
                      resolveRemoteAuthorRole={resolveRemoteAuthorRole}
                      showAuthorName={showAuthorName}
                      showAuthorAvatar={showAuthorAvatar}
                      reserveAuthorRail={isWideDesktopThreadLayout}
                      leadingAvatarPrimary={railLead.primary}
                      leadingAvatarFallback={railLead.fallback}
                      leadingAvatarName={isRemote ? resolveRemoteAuthor(message.from_id) : currentUsername}
                    />
                  </div>
                );
              })}
              <div ref={oldestSentinelRef} className="chat-shell__oldest-sentinel" aria-hidden />
            </>
          )}
        </div>
        <div className="chat-shell__composer">
          {chatReplyTo ? (
            <div className="chat-shell__reply-bar">
              <div className="chat-shell__reply-bar-inner">
                <span className="chat-shell__reply-bar-label">
                  ↩{" "}
                  {chatReplyTo.from_id === currentUserId
                    ? `@${truncate(currentUsername, 18)}`
                    : `@${truncate(resolveRemoteAuthor(chatReplyTo.from_id), 18)}`}
                </span>
                <span className="chat-shell__reply-bar-text">{chatReplyTo.previewText}</span>
              </div>
              <button
                type="button"
                className="chat-shell__reply-bar-cancel"
                onClick={onCancelReply}
                aria-label="Отменить ответ"
              >
                ×
              </button>
            </div>
          ) : null}
          {renderComposerDraft()}
          {!chatAttachmentDraft && uploadBusy ? renderUploadStatus() : null}
          <div className="chat-shell__composer-row chat-shell__composer-row--multiline">
            {supportsComposerAttachments ? renderComposerAttachControls() : null}
            <span className="chat-shell__prompt">{">"}</span>
            <textarea
              ref={chatInputRef}
              rows={1}
              autoComplete="off"
              enterKeyHint={isMobile ? "enter" : undefined}
              value={chatInput}
              onChange={(e) => onChatInput(e.target.value)}
              onKeyDown={(e) => onComposerKeyDown(e, canSendWs)}
              onPaste={handleComposerPaste}
              placeholder="сообщение…"
              className="chat-shell__input chat-shell__input--multiline"
              aria-multiline
            />
            {showMicWs ? (
              <VoiceRecorderButton
                onVoiceReady={async (result) => {
                  const buf = await result.blob.arrayBuffer();
                  onSendVoice!(new Uint8Array(buf), result.mime, result.durationMs, result.waveform);
                }}
                onError={(msg) => dispatchChat({ type: "set-error", error: msg })}
                disabled={uploadBusy}
              />
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled={!canSendWs}
                className={`chat-shell__send ${canSendWs ? "is-active" : ""}`}
                aria-label="Отправить"
              >
                <AppIcon name="send-horizontal" className="chat-shell__send-icon" />
              </button>
            )}
          </div>
        </div>
        <WorkspaceMembersPanel
          workspaceId={workspaceTopicView.workspaceId}
          currentUserId={currentUserId}
          open={workspaceMembersOpen}
          onClose={() => setWorkspaceMembersOpen(false)}
        />
      </div>
    );
  }

  if (!effectiveContact) {
    return (
      <div className="chat-shell chat-shell--empty">
        <div className="chat-empty">
          <span className="chat-empty__hint">// выберите контакт</span>
        </div>
      </div>
    );
  }

  const {
    isE2E,
    supportsTopics,
    supportsRoles,
    supportsReadReceipts,
  } = conversationCapabilities;

  return (
    <div className={`chat-shell${vaultTopicView ? " chat-shell--vault" : ""}`}>
      {isMobile ? (
        vaultTopicView ? (
          <MobileEntityHeader
            onBack={onBack}
            title={truncate(vaultTopicView.vaultName, 22)}
            titleTitle={vaultTopicView.vaultName}
            overflowItems={dmOrVaultOverflowItems}
            onPinnedMessages={
              supportsMessageActions ? () => setPinnedMessagesPanelOpen((v) => !v) : undefined
            }
            pinnedCount={chatState.pinnedMessages.length}
            dataSupportsTopics={supportsTopics}
            dataSupportsRoles={supportsRoles}
          />
        ) : (
          <MobileEntityHeader
            onBack={onBack}
            title={`@${truncate(effectiveContact.username, 18)}`}
            titleTitle={`@${effectiveContact.username}`}
            titleSuffix={<span className={`presence-dot presence-dot--${presenceStatus}`} aria-hidden="true" />}
            showCall={Boolean(voiceCallEnabled && onVoiceCall)}
            onCall={onVoiceCall}
            overflowItems={dmOrVaultOverflowItems}
            onPinnedMessages={
              supportsMessageActions ? () => setPinnedMessagesPanelOpen((v) => !v) : undefined
            }
            pinnedCount={chatState.pinnedMessages.length}
            dataSupportsTopics={supportsTopics}
            dataSupportsRoles={supportsRoles}
          />
        )
      ) : (
        <div
          className="chat-shell__head"
          data-supports-topics={supportsTopics ? "1" : "0"}
          data-supports-roles={supportsRoles ? "1" : "0"}
        >
          <span
            className="chat-shell__contact"
            title={vaultTopicView ? vaultTopicView.vaultName : `@${effectiveContact.username}`}
          >
            {vaultTopicView ? truncate(vaultTopicView.vaultName, 22) : `@${truncate(effectiveContact.username, 10)}`}
          </span>
          {vaultTopicView ? null : (
            <span className={`presence-dot presence-dot--${presenceStatus}`} aria-hidden="true" />
          )}
          <button
            type="button"
            className="chat-shell__head-icon-btn"
            title={
              vaultTopicView
                ? isPinned("vault", vaultTopicView.vaultId) ? "Открепить хранилище" : "Закрепить хранилище"
                : isPinned("contact", effectiveContact.user_id) ? "Открепить контакт" : "Закрепить контакт"
            }
            aria-label={
              vaultTopicView
                ? isPinned("vault", vaultTopicView.vaultId) ? "Открепить хранилище" : "Закрепить хранилище"
                : isPinned("contact", effectiveContact.user_id) ? "Открепить контакт" : "Закрепить контакт"
            }
            onClick={() =>
              vaultTopicView
                ? togglePin("vault", vaultTopicView.vaultId)
                : togglePin("contact", effectiveContact.user_id)
            }
          >
            <AppIcon
              name={
                vaultTopicView
                  ? isPinned("vault", vaultTopicView.vaultId) ? "pin-off" : "pin"
                  : isPinned("contact", effectiveContact.user_id) ? "pin-off" : "pin"
              }
              className="chat-shell__head-icon-svg"
            />
          </button>
          {supportsMessageActions ? (
            <button
              type="button"
              className="chat-shell__head-icon-btn chat-shell__head-icon-btn--with-badge"
              title="Закреплённые сообщения"
              aria-label="Закреплённые сообщения"
              onClick={() => setPinnedMessagesPanelOpen((v) => !v)}
            >
              <AppIcon name="pin" className="chat-shell__head-icon-svg" />
              {chatState.pinnedMessages.length > 0 ? (
                <span className="chat-shell__head-badge">{chatState.pinnedMessages.length}</span>
              ) : null}
            </button>
          ) : null}
          {!vaultTopicView && voiceCallEnabled && onVoiceCall ? (
            <button
              type="button"
              className="chat-shell__call"
              onClick={onVoiceCall}
              aria-label="Голосовой звонок"
              title="Звонок"
            >
              <AppIcon name="phone" className="chat-shell__call-icon" />
            </button>
          ) : null}
        </div>
      )}
      {pinToast ? (
        <div className="chat-shell__pin-toast" role="status">
          {pinToast}
        </div>
      ) : null}
      {isE2E ? (
        <div className="chat-shell__banner">
          {vaultTopicView
            ? "◆ VAULT // secretbox // XSalsa20-Poly1305 ◆"
            : "◆ STXR // crypto_box_easy // X25519 ◆"}
        </div>
      ) : null}
      {pinnedMessagesPanelOpen && supportsMessageActions ? (
        <PinnedMessagesPanel
          pins={pinnedMessagesForPanel}
          onClose={() => setPinnedMessagesPanelOpen(false)}
          onSelectMessageId={(id) => {
            setPinnedMessagesPanelOpen(false);
            setPinnedJumpMessageId(id);
          }}
        />
      ) : null}
      {isMobile ? (
        <div className="chat-shell__reply-hint" aria-hidden="true">
          тап по сообщению → реакции и reply · свайп → быстрый reply
        </div>
      ) : null}
      <div className="chat-shell__body" ref={bodyRef} onScroll={onBodyScroll}>
        {conversationLoading && messages.length === 0 ? (
          renderConversationLoadingState()
        ) : visibleMessages.length === 0 ? (
          <div className="chat-empty chat-empty--inline">
            <span className="chat-empty__hint">
              {messages.length > 0
                ? "// в этом фрагменте только служебные сообщения (реакции) — прокрутите выше или ниже"
                : vaultTopicView
                  ? "// нет сообщений — запишите заметку"
                  : isE2E
                    ? `// начните шифрованный чат с @${effectiveContact.username}`
                    : `// начните чат с @${effectiveContact.username}`}
            </span>
          </div>
        ) : (
          <>
            <div
              ref={newestSentinelRef}
              className="chat-shell__newest-sentinel"
              aria-hidden
            />
            {visibleNewestFirst.map((message, index) => {
            const rid = envelopeReplyToId(message.plaintext);
            const replyParent =
              rid === null ? undefined : (messages.find((m) => m.id === rid) ?? null);
            const newerMessage = index > 0 ? visibleNewestFirst[index - 1] : undefined;
            const showAuthorAvatar = isWideDesktopThreadLayout && newerMessage?.from_id !== message.from_id;
            const mine = message.from_id === currentUserId;
            const dmRailLead = mine ? currentUserAvatarSources : selectedPeerAvatarSources;
            return (
              <div
                key={message.id}
                ref={(el) => {
                  msgRefs.current[message.id] = el;
                }}
                className={`bubble-row ${isWideDesktopThreadLayout ? "bubble-row--compact" : ""}`}
                data-message-id={message.id}
              >
                <MessageBubble
                  message={message}
                  ownUserId={currentUserId}
                  ownUsername={currentUsername}
                  peerUsername={effectiveContact.username}
                  supportsReadReceipts={supportsReadReceipts}
                  plaintext={message.plaintext}
                  replyParent={replyParent}
                  isHighlighted={flashMessageId === message.id}
                  isMobile={isMobile}
                  reactions={reactionsByMessageId[message.id] ?? []}
                  isMenuOpen={supportsMessageActions ? menuMessageId === message.id : false}
                  onToggleMenu={() =>
                    setMenuMessageId((current) => (current === message.id ? null : message.id))
                  }
                  onCloseMenu={() => setMenuMessageId((current) => (current === message.id ? null : current))}
                  onReply={onReplyToMessage}
                  onForward={onForwardMessage}
                  onToggleReaction={onToggleReaction}
                  onScrollToMessage={scrollToMessage}
                  supportsMessageActions={supportsMessageActions}
                  isMessagePinned={pinnedMessageIds.has(message.id)}
                  onTogglePinMessage={supportsMessageActions ? handleTogglePinMessage : undefined}
                  canDeleteMessage={canDeleteMessage(message)}
                  onDeleteMessage={() => void deleteMessage(message)}
                  onRetryFailedOutbox={(m) => void retryFailedOutboxSend(m.id)}
                  fetchE2eEncryptedBlob={vaultTopicView ? fetchE2eEncryptedBlob : undefined}
                  showAuthorAvatar={showAuthorAvatar}
                  reserveAuthorRail={isWideDesktopThreadLayout}
                  leadingAvatarPrimary={dmRailLead.primary}
                  leadingAvatarFallback={dmRailLead.fallback}
                  leadingAvatarName={mine ? currentUsername : effectiveContact.username}
                />
              </div>
            );
          })}
            <div ref={oldestSentinelRef} className="chat-shell__oldest-sentinel" aria-hidden />
          </>
        )}
      </div>
      <div className="chat-shell__composer">
        {chatReplyTo ? (
          <div className="chat-shell__reply-bar">
            <div className="chat-shell__reply-bar-inner">
              <span className="chat-shell__reply-bar-label">
                ↩{" "}
                {chatReplyTo.from_id === currentUserId
                  ? `@${truncate(currentUsername, 18)}`
                  : `@${truncate(effectiveContact.username, 18)}`}
              </span>
              <span className="chat-shell__reply-bar-text">{chatReplyTo.previewText}</span>
            </div>
            <button
              type="button"
              className="chat-shell__reply-bar-cancel"
              onClick={onCancelReply}
              aria-label="Отменить ответ"
            >
              ×
            </button>
          </div>
        ) : null}
        {renderComposerDraft()}
        {!chatAttachmentDraft && uploadBusy ? renderUploadStatus() : null}
        <div className="chat-shell__composer-row chat-shell__composer-row--multiline">
          {renderComposerAttachControls()}
          <span className="chat-shell__prompt">{">"}</span>
          <textarea
            ref={chatInputRef}
            rows={1}
            autoComplete="off"
            enterKeyHint={isMobile ? "enter" : undefined}
            value={chatInput}
            onChange={(e) => onChatInput(e.target.value)}
            onKeyDown={(e) => onComposerKeyDown(e, canSend)}
            onPaste={handleComposerPaste}
            placeholder="сообщение…"
            className="chat-shell__input chat-shell__input--multiline"
            aria-multiline
          />
          {showMicButton ? (
            <VoiceRecorderButton
              onVoiceReady={async (result) => {
                const buf = await result.blob.arrayBuffer();
                onSendVoice!(new Uint8Array(buf), result.mime, result.durationMs, result.waveform);
              }}
              onError={(msg) => dispatchChat({ type: "set-error", error: msg })}
              disabled={uploadBusy}
            />
          ) : (
            <button
              type="button"
              onClick={onSend}
              disabled={!canSend}
              className={`chat-shell__send ${canSend ? "is-active" : ""}`}
              aria-label="Отправить"
            >
              <AppIcon name="send-horizontal" className="chat-shell__send-icon" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
